"use client";

import { useState, useEffect } from "react";
import { getRelatedGames } from "@/lib/games";
import GameGrid from "@/components/game-grid";
import type { GameType } from "@/lib/types";
import { Sparkles, Loader2 } from "lucide-react"; // `Loader2` 및 `Sparkles` 추가

interface RelatedGamesProps {
  currentGameId: string;
}

// 로딩 인디케이터 컴포넌트
function LoadingIndicator() {
  return (
    <div className="text-center py-4 text-primary">
      <Loader2 className="h-8 w-8 animate-spin mx-auto" />
      <p className="mt-2">관련 게임을 불러오는 중...</p>
    </div>
  );
}

export function RelatedGames({ currentGameId }: RelatedGamesProps) {
  const [relatedGames, setRelatedGames] = useState<GameType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRelatedGames = async () => {
      setLoading(true);
      setError(null);
      try {
        const relatedGamesData = await getRelatedGames(currentGameId);
        setRelatedGames(relatedGamesData);
      } catch (err) {
        console.error("관련 게임 데이터 로딩 실패:", err);
        setError("관련 게임 데이터를 불러오는 데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedGames();
  }, [currentGameId]);

  if (loading) {
    return (
      <section className="mt-6">
        <h2 className="text-2xl font-bold text-primary flex items-center mb-4">
          <Sparkles className="mr-2 h-6 w-6" /> 관련 게임
        </h2>
        <LoadingIndicator />
      </section>
    );
  }

  if (error) {
    return (
      <section className="mt-6">
        <h2 className="text-2xl font-bold text-primary flex items-center mb-4">
          <Sparkles className="mr-2 h-6 w-6" /> 관련 게임
        </h2>
        <div className="text-center text-destructive border rounded-lg bg-destructive/10 p-4">
          <p>{error}</p>
        </div>
      </section>
    );
  }

  if (relatedGames.length === 0) {
    return null;
  }

  return (
    <section className="mt-6">
      <h2 className="text-2xl font-bold text-primary flex items-center mb-4">
        <Sparkles className="mr-2 h-6 w-6" /> 관련 게임
      </h2>
      <GameGrid
        games={relatedGames}
        categories={[]}
        selectedCategory={null}
        expandable={false}
        unit={12}
      />
    </section>
  );
}
